import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Button } from "react-bootstrap";
import AuthApi from "../../api/authApi";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import { useNavigate, useLocation, Link } from "react-router-dom";
import AuthShell from "./AuthShell";
import { trimIfOnChangeField } from "../../utils/trimFormFields";
import { applyLoginSession, isLoggingOut } from "../../utils/authSession";
import { authClient } from "../../auth/authClient";

interface LoginProps {
  updatedUser?: (role: string) => void;
}

interface LoginFormData {
  username: string;
  password: string;
  signedIn: boolean;
  [key: string]: any;
}

const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  no_account:
    "This Google email is not associated with a LOVESTRONG account.",
  inactive:
    "Your account is not active. Please contact Lovestrong Admin.",
  signup_disabled:
    "This Google email is not associated with a LOVESTRONG account.",
};

const Login = ({ updatedUser }: LoginProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectPath = queryParams.get("redirect");
  const api = useRef(new AuthApi()).current;

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [data, setData] = useState<LoginFormData>({
    username: "",
    password: "",
    signedIn: false,
  });
  const [validated, setValidated] = useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const googleReturnHandled = useRef(false);
  // eslint-disable-next-line
  const [cookies, setCookie] = useCookies(["LoveStrongUser"]);

  const clearGoogleQuery = useCallback(() => {
    const next = new URLSearchParams(location.search);
    if (!next.has("google") && !next.has("error")) return;
    next.delete("google");
    next.delete("error");
    const search = next.toString();
    navigate(
      { pathname: "/login", search: search ? `?${search}` : "" },
      { replace: true },
    );
  }, [location.search, navigate]);

  const finishLogin = useCallback(
    (response: any) => {
      applyLoginSession(response, setCookie, {
        updatedUser,
        navigate,
        redirectPath,
        persist: Boolean(data.signedIn),
      });
    },
    [data.signedIn, navigate, redirectPath, setCookie, updatedUser],
  );

  const completeGoogleSession = useCallback(async () => {
    if (isLoggingOut()) return;
    setGoogleLoading(true);
    try {
      const response = await api.completeSession();
      if (isLoggingOut()) return;
      if (response && response.success) {
        finishLogin(response);
      } else {
        toast.error(
          response?.unauthenticated
            ? "Google sign-in failed. Please try again."
            : (response && response.msg) ||
                "This Google email is not associated with a LOVESTRONG account.",
        );
        clearGoogleQuery();
      }
    } catch (error) {
      console.error(error);
      toast.error("Google sign-in failed. Please try again.");
      clearGoogleQuery();
    } finally {
      setGoogleLoading(false);
    }
  }, [api, clearGoogleQuery, finishLogin]);

  useEffect(() => {
    if (googleReturnHandled.current || isLoggingOut()) return;
    const googleFlag = queryParams.get("google");
    const errorCode = queryParams.get("error");
    if (errorCode || googleFlag === "error") {
      googleReturnHandled.current = true;
      toast.error(
        GOOGLE_ERROR_MESSAGES[errorCode || ""] ||
          "Google sign-in failed. Please try again.",
      );
      clearGoogleQuery();
      return;
    }
    if (googleFlag === "1") {
      googleReturnHandled.current = true;
      completeGoogleSession();
    }
  }, [clearGoogleQuery, completeGoogleSession, location.search]);

  const handleGoogleSignIn = async () => {
    if (googleLoading || passwordLoading) return;
    setGoogleLoading(true);
    try {
      const callbackURL = `${window.location.origin}/login?google=1`;
      const errorCallbackURL = `${window.location.origin}/login?google=error`;
      await authClient.signIn.social({
        provider: "google",
        callbackURL,
        errorCallbackURL,
        requestSignUp: true,
      });
    } catch (error) {
      console.error(error);
      setGoogleLoading(false);
      toast.error("Google sign-in failed. Please try again.");
    }
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    if (googleLoading || passwordLoading) return;
    if (form.checkValidity() === true) {
      setPasswordLoading(true);
      try {
        const response: any = await api.Login(data);
        if (response.success) {
          finishLogin(response);
        } else {
          toast.error(response.msg || "Login failed.");
        }
      } catch (error) {
        console.log(error);
      } finally {
        setPasswordLoading(false);
      }
    } else {
      toast.error("User Name and Password is required");
    }
    setValidated(true);
  };

  const handleChanges = (e: React.ChangeEvent<any>) => {
    const { name, type, checked, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : typeof value === "string"
            ? trimIfOnChangeField(name, value)
            : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const busy = googleLoading || passwordLoading;

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to continue to LoveStrong.">
      <Form
        onSubmit={handleLoginSubmit}
        noValidate
        validated={validated}
        className="auth-modern-form"
      >
        <Form.Group className="auth-field">
          <Form.Label>Username or Email</Form.Label>
          <Form.Control
            required
            className="form-control form-control-sm border-left-0"
            type="text"
            name="username"
            id=""
            placeholder="Enter username or email"
            value={data.username}
            onChange={(e) => handleChanges(e)}
            disabled={busy}
          />
          <Form.Control.Feedback type="invalid">
            Username or email is required!
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="auth-field">
          <Form.Label>Password</Form.Label>
          <div className="auth-password-wrap">
            <Form.Control
              required
              type={showPassword ? "text" : "password"}
              name="password"
              className="form-control form-control-sm border-left-0"
              id="exampleInputPassword"
              placeholder="Enter password"
              value={data.password}
              onChange={(e) => handleChanges(e)}
              disabled={busy}
            />
            <i
              onClick={togglePasswordVisibility}
              className={`mdi ${
                showPassword ? "mdi-eye" : "mdi-eye-off"
              } auth-eye${validated ? " auth-eye-validated" : ""}`}
              role="button"
              tabIndex={0}
              onKeyPress={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  togglePasswordVisibility();
                }
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            />
            <Form.Control.Feedback type="invalid">
              Password is required!
            </Form.Control.Feedback>
          </div>
        </Form.Group>

        <div className="mt-3 mb-1">
          <Button type="submit" className="auth-submit" id="LoginBtn" disabled={busy}>
            {passwordLoading ? "Signing in…" : "Sign In"}
          </Button>
        </div>

        <div className="auth-meta-row">
          <Form.Group className="check">
            <Form.Check
              type="checkbox"
              label="Keep me signed in"
              name="signedIn"
              id="signedIn"
              feedback="You must agree before submitting."
              feedbackType="invalid"
              onChange={(e) => handleChanges(e)}
            />
          </Form.Group>
          <Link
            to="/forgetPassword"
            className="auth-link"
            onKeyPress={(e: React.KeyboardEvent) =>
              e.key === "Enter"
                ? (document.getElementById("LoginBtn") as HTMLButtonElement).click()
                : null
            }
          >
            Forgot Password?
          </Link>
        </div>
      </Form>

      <div className="auth-alt">
        <div className="auth-divider" role="separator">
          <span>OR</span>
        </div>

        <div className={`auth-google${googleLoading ? " is-loading" : ""}`}>
          <button
            type="button"
            className="auth-google-btn"
            onClick={handleGoogleSignIn}
            disabled={busy}
          >
            <span className="auth-google-logo" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </span>
            <span className="auth-google-label">
              {googleLoading ? "Signing in…" : "Continue with Google"}
            </span>
          </button>
        </div>

        <p className="auth-google-note">
          <span className="auth-google-note-icon" aria-hidden="true">
            <i className="mdi mdi-shield-check-outline" />
          </span>
          <span>
            Use Google only to sign into an already-existing LOVESTRONG
            account.
          </span>
        </p>
      </div>
    </AuthShell>
  );
};

export default Login;
